import { useEffect, type ReactNode } from "react";
import type { InteractionProps } from "./interfaces";
import { formatDateString } from "../../../utils/formatDateString";
import {
  InteractionPaymentDayCell,
  InteractionDurationCell,
  InteractionFeeCell,
  InteractionStatusCell,
  type InteractionListEditContext,
} from "./interactionListEditCells";

export type { InteractionListEditContext };

export const INTERACTIONS_TABLE_COLUMNS_STORAGE_KEY =
  "trust-insight-interactions-table-columns-visibility";

/** Partes do texto da coluna Assets para filtro (cadastro: múltipla escolha, ex. "Truxt, BRAM"). */
export function splitAssetsCellValues(value: string | null | undefined): string[] {
  if (value == null) return [];
  const s = String(value).trim();
  if (!s) return [];
  return s
    .split(",")
    .map((part) => part.trim())
    .filter((part) => part.length > 0);
}

/** Iniciais do nome do pesquisador (coluna RA na listagem). */
export function formatRaNameInitials(
  researcherName: string | null | undefined,
): string {
  const s = String(researcherName ?? "").trim();
  if (!s) return "-";
  const words = s.split(/\s+/).filter((w) => w.length > 0);
  const initials = words
    .map((w) => {
      const ch = w.charAt(0);
      return ch ? ch.toLocaleUpperCase("pt-BR") : "";
    })
    .join("");
  return initials || "-";
}

/** Exibe (duration × valor/hora) / 60 formatado para pt-BR. */
export function formatValorSerPagoDisplay(value: number | null | undefined): string {
  if (value == null || Number.isNaN(Number(value))) return "-";
  return Number(value).toLocaleString("pt-BR", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

export function formatListDate(value: string | null | undefined): string {
  if (value == null || String(value).trim() === "") return "-";
  const raw = String(value).trim();
  // Evita deslocamento de fuso para datas de calendário vindas como YYYY-MM-DD
  // ou YYYY-MM-DDTHH:mm:ss... (inclui casos serializados do backend).
  const isoDatePrefix = raw.match(/^(\d{4})-(\d{2})-(\d{2})(?:$|T)/);
  if (isoDatePrefix) {
    const y = isoDatePrefix[1];
    const m = isoDatePrefix[2];
    const d = isoDatePrefix[3];
    return `${d}/${m}/${y}`;
  }
  const brDate = raw.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
  if (brDate) {
    return raw;
  }
  try {
    return formatDateString(raw, {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  } catch {
    return raw;
  }
}

export function renderProjectColumn(doc: InteractionProps): ReactNode {
  const projectNumber = (doc.projectNumber ?? doc.projeto ?? "").trim();
  const asanaUrl = doc.asanaProjectUrl?.trim() ?? "";
  if (asanaUrl) {
    return (
      <a
        href={asanaUrl}
        target="_blank"
        rel="noopener noreferrer"
        style={{
          color: "#76A6C2",
          textDecoration: "underline",
          fontWeight: 500,
        }}
        title={asanaUrl}
        onClick={(e) => e.stopPropagation()}
      >
        {projectNumber || asanaUrl}
      </a>
    );
  }
  return projectNumber || "-";
}

/** Última coluna da faixa congelada no scroll horizontal da listagem (inclusive). */
const FROZEN_UNTIL_FIELD = "nome";

export function isColumnFrozenThroughStatus(field: string): boolean {
  const end = MEETING_TABLE_COLUMN_SPECS.findIndex((s) => s.field === FROZEN_UNTIL_FIELD);
  const i = MEETING_TABLE_COLUMN_SPECS.findIndex((s) => s.field === field);
  return i !== -1 && end !== -1 && i <= end;
}

/** Larguras fixas alinhadas ao `minWidth` da tabela para cálculo de `left` no sticky. */
export const ACTIONS_COL_WIDTH_PX = 62;
export const DATA_COL_WIDTH_PX = 90;

/** `left` em px para células de dados congeladas; `null` se a coluna não congela. */
export function frozenDataColumnLeftPx(
  field: string,
  visibleFieldsInOrder: string[],
): number | null {
  if (!isColumnFrozenThroughStatus(field)) return null;
  let left = ACTIONS_COL_WIDTH_PX;
  for (const f of visibleFieldsInOrder) {
    if (f === field) return left;
    if (isColumnFrozenThroughStatus(f)) left += DATA_COL_WIDTH_PX;
  }
  return null;
}

/** Último campo visível que ainda está na faixa congelada (para sombra à direita). */
export function lastFrozenVisibleField(
  visibleFieldsInOrder: string[],
): string | null {
  let last: string | null = null;
  for (const f of visibleFieldsInOrder) {
    if (isColumnFrozenThroughStatus(f)) last = f;
  }
  return last;
}

export const MEETING_TABLE_COLUMN_SPECS: { field: string; headerName: string }[] =
  [
    { field: "id", headerName: "ID" },
    { field: "ra", headerName: "RA" },
    { field: "cliente", headerName: "Cliente" },
    { field: "projeto", headerName: "Projeto" },
    { field: "data", headerName: "Data" },
    { field: "hora", headerName: "Hora (BRT)" },
    { field: "nome", headerName: "Nome" },
    { field: "empresa", headerName: "Empresa" },
    { field: "status", headerName: "Status" },
    { field: "duracao", headerName: "Duração (min)" },
    { field: "modeloInteracao", headerName: "Modelo de interação" },
    { field: "tipoInteracao", headerName: "Tipo de interação" },
    { field: "assets", headerName: "Assets" },
    { field: "analistas", headerName: "Analistas" },
    { field: "proativo", headerName: "Proativo" },
    { field: "tipo", headerName: "Tipo" },
    { field: "atraso", headerName: "Atraso" },
    { field: "atrasoMin", headerName: "Atraso (min)" },
    { field: "moeda", headerName: "Moeda" },
    { field: "feeCredito", headerName: "Fee BRL" },
    { field: "paymentDeadline", headerName: "Deadline Pagamento" },
    { field: "paymentDay", headerName: "Data de pagamento" },
    { field: "valorSerPago", headerName: "Valor a ser pago" },
    { field: "nomeCompleto", headerName: "Nome Completo ou Razão Social" },
    { field: "cpf", headerName: "CPF ou CNPJ" },
    { field: "tedOuPix", headerName: "TED ou PIX" },
    { field: "banco", headerName: "Banco" },
    { field: "agencia", headerName: "Agência" },
    { field: "conta", headerName: "Conta" },
    { field: "pix", headerName: "PIX" },
    { field: "dataNascimento", headerName: "Data de Nascimento" },
    { field: "rg", headerName: "RG" },
    { field: "pais", headerName: "País" },
    { field: "endereco", headerName: "Endereço" },
    { field: "municipio", headerName: "Município" },
    { field: "cep", headerName: "CEP" },
    { field: "duracaoReal", headerName: "Duração Real (min)" },
    { field: "feedback", headerName: "Feedback" },
    { field: "observacao", headerName: "Observação" },
  ];

export function renderMeetingDataCell(
  field: string,
  doc: InteractionProps,
  editCtx?: InteractionListEditContext | null,
): ReactNode {

  switch (field) {
    case "id":
      return doc.id;
    case "ra": {
      const full = String(doc.ra ?? "").trim();
      const initials = formatRaNameInitials(doc.ra);
      if (!full) return "-";
      return (
        <span title={full} style={{ cursor: "default" }}>
          {initials}
        </span>
      );
    }
    case "cliente":
      return doc.cliente;
    case "projeto":
      return renderProjectColumn(doc);
    case "data":
      return formatListDate(doc.data);
    case "hora":
      return doc.hora;
    case "nome":
      return doc.nome;
    case "empresa":
      return doc.empresa;
    case "status":
      if (editCtx?.onPatch) {
        return <InteractionStatusCell doc={doc} ctx={editCtx} />;
      }
      return doc.status;
    case "duracao":
      if (editCtx?.onPatch) {
        return <InteractionDurationCell doc={doc} ctx={editCtx} />;
      }
      return doc.duracao;
    case "valorSerPago":
      return formatValorSerPagoDisplay(doc.valorSerPago);
    case "modeloInteracao":
      return doc.modeloInteracao;
    case "tipoInteracao":
      return doc.tipoInteracao;
    case "assets":
      return doc.assets;
    case "analistas":
      return doc.analistas;
    case "proativo":
      return doc.proativo ?? "-";
    case "tipo":
      return doc.tipo ?? "-";
    case "atraso":
      return doc.atraso ?? "-";
    case "atrasoMin":
      return doc.atrasoMin ?? "-";
    case "moeda":
      return doc.moeda;
    case "feeCredito":
      if (editCtx?.onPatch) {
        return <InteractionFeeCell doc={doc} ctx={editCtx} />;
      }
      return doc.feeCredito;
    case "nomeCompleto":
      return doc.nomeCompleto ?? doc.nome;
    case "cpf":
      return doc.cpf || "-";
    case "paymentDeadline":
      return formatListDate(doc.deadlinePgto);
    case "paymentDay":
      if (editCtx?.onPatch) {
        return <InteractionPaymentDayCell doc={doc} ctx={editCtx} />;
      }
      return formatListDate(doc.paymentDay);
    case "tedOuPix":
      return doc.tedOuPix;
    case "banco":
      return doc.banco ?? "-";
    case "agencia":
      return doc.agencia ?? "-";
    case "conta":
      return doc.conta ?? "-";
    case "pix":
      return doc.pix ?? "-";
    case "dataNascimento":
      return doc.dataNascimento ?? "-";
    case "rg":
      return doc.rg ?? "-";
    case "pais":
      return doc.pais ?? "-";
    case "endereco":
      return doc.endereco ?? "-";
    case "municipio":
      return doc.municipio ?? "-";
    case "cep":
      return doc.cep ?? "-";
    case "duracaoReal":
      return doc.duracaoReal;
    case "feedback":
      return doc.feedback ?? "-";
    case "observacao":
      return doc.observacao ?? "-";
    default:
      return "-";
  }
}

/** Versão textual da célula para busca/filtro por coluna na listagem. */
export function getMeetingDataCellFilterText(
  field: string,
  doc: InteractionProps,
): string {
  switch (field) {
    case "id":
      return String(doc.id ?? "");
    case "ra":
      return String(doc.ra ?? "");
    case "cliente":
      return String(doc.cliente ?? "");
    case "projeto":
      return String(doc.projectNumber ?? "").trim();
    case "data": {
      const formatted = formatListDate(doc.data);
      return formatted === "-" ? "" : formatted;
    }
    case "hora":
      return String(doc.hora ?? "");
    case "nome":
      return String(doc.nome ?? "");
    case "empresa":
      return String(doc.empresa ?? "");
    case "status":
      return String(doc.status ?? "");
    case "duracao":
      return String(doc.duracao ?? "");
    case "valorSerPago":
      return doc.valorSerPago != null && !Number.isNaN(Number(doc.valorSerPago))
        ? formatValorSerPagoDisplay(doc.valorSerPago)
        : "";
    case "modeloInteracao":
      return String(doc.modeloInteracao ?? "");
    case "tipoInteracao":
      return String(doc.tipoInteracao ?? "");
    case "assets":
      return String(doc.assets ?? "");
    case "analistas":
      return String(doc.analistas ?? "");
    case "proativo":
      return String(doc.proativo ?? "");
    case "tipo":
      return String(doc.tipo ?? "");
    case "atraso":
      return String(doc.atraso ?? "");
    case "atrasoMin":
      return String(doc.atrasoMin ?? "");
    case "moeda":
      return String(doc.moeda ?? "");
    case "feeCredito":
      return String(doc.feeCredito ?? "");
    case "nomeCompleto":
      return String(doc.nomeCompleto ?? doc.nome ?? "");
    case "cpf":
      return String(doc.cpf ?? "");
    case "paymentDeadline": {
      const formatted = formatListDate(doc.deadlinePgto);
      return formatted === "-" ? "" : formatted;
    }
    case "paymentDay": {
      const formatted = formatListDate(doc.paymentDay);
      return formatted === "-" ? "" : formatted;
    }
    case "tedOuPix":
      return String(doc.tedOuPix ?? "");
    case "banco":
      return String(doc.banco ?? "");
    case "agencia":
      return String(doc.agencia ?? "");
    case "conta":
      return String(doc.conta ?? "");
    case "pix":
      return String(doc.pix ?? "");
    case "dataNascimento":
      return String(doc.dataNascimento ?? "");
    case "rg":
      return String(doc.rg ?? "");
    case "pais":
      return String(doc.pais ?? "");
    case "endereco":
      return String(doc.endereco ?? "");
    case "municipio":
      return String(doc.municipio ?? "");
    case "cep":
      return String(doc.cep ?? "");
    case "duracaoReal":
      return String(doc.duracaoReal ?? "");
    case "feedback":
      return String(doc.feedback ?? "");
    case "observacao":
      return String(doc.observacao ?? "");
    default:
      return "";
  }
}

/** Texto plano para exportação Excel (ex.: projeto com fallback legível). */
export function getMeetingDataCellExportText(
  field: string,
  doc: InteractionProps,
): string {
  switch (field) {
    case "projeto":
      return `${(doc.projectNumber ?? doc.projeto ?? "").trim()}`;
    case "data":
      return formatListDate(doc.data);
    case "paymentDay":
      return formatListDate(doc.paymentDay);
    case "dataNascimento":
      return doc.dataNascimento ? formatListDate(doc.dataNascimento) : "-";
    case "valorSerPago":
      return formatValorSerPagoDisplay(doc.valorSerPago);
    default:
      return getMeetingDataCellFilterText(field, doc) || "-";
  }
}
